import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdDelete, MdEdit } from "react-icons/md";
import { getPlacementCountDetailsapi, editPlacementCountPage, deletePlacementCountApi, addPlacementCountApi } from "Services/allApi";
import PlacementProcess from "./PlacementProcess";

const PlacementCount = () => {
    const [activeTab, setActiveTab] = useState("main");
    const [placementCounts, setPlacementCounts] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCount, setSelectedCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState("");
    const [count, setCount] = useState("");
    const [editYear, setEditYear] = useState("");
    const [editCount, setEditCount] = useState("");

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => {
        setIsModalOpen(false);
        setYear("");
        setCount("");
    };

    const openEditModal = (count) => {
        setSelectedCount(count);
        setEditYear(count.year || "");
        setEditCount(count.count || "");
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedCount(null);
    };

    const fetchPlacementCounts = async () => {
        try {
            setLoading(true);
            const response = await getPlacementCountDetailsapi();
            if (response?.status === 200 && response.data) {
                setPlacementCounts(response.data);
            } else {
                toast.error('Failed to fetch data');
                setPlacementCounts([]);
            }
        } catch (error) {
            console.error("Error fetching placement counts:", error);
            toast.error('Error fetching data');
            setPlacementCounts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!year.trim() || !count.trim()) {
            toast.error("Year and count are required!");
            return;
        }

        const newCountData = { year, count };

        try {
            const response = await addPlacementCountApi(newCountData);
            if (response?.status === 200) {
                toast.success("Placement count added successfully!");
                closeModal();
                fetchPlacementCounts();
            } else {
                toast.error("Failed to add placement count.");
            }
        } catch (error) {
            console.error("Error adding placement count:", error);
            toast.error("Error adding data.");
        }
    };

    const handleEditSave = async () => {
        if (!selectedCount) return;

        const updatedData = { year: editYear, count: editCount };

        try {
            const response = await editPlacementCountPage(selectedCount.id, updatedData);
            if (response?.status === 200) {
                toast.success("Placement count updated successfully!");
                closeEditModal();
                fetchPlacementCounts();
            } else {
                toast.error("Failed to update placement count.");
            }
        } catch (error) {
            console.error("Error updating placement count:", error);
            toast.error("Error updating data.");
        }
    };

    const handleDeleteCount = async (id) => {
        try {
            const response = await deletePlacementCountApi(id);
            if (response?.status === 204) {
                toast.success("Placement count deleted successfully!");
                fetchPlacementCounts();
            } else {
                toast.error("Failed to delete placement count.");
            }
        } catch (error) {
            console.error("Error deleting placement count:", error);
            toast.error("Error deleting data.");
        }
    };

    useEffect(() => {
        fetchPlacementCounts();
    }, []);

    return (
        <div className="w-full max-w-6xl mx-auto p-8 bg-white shadow-lg rounded-lg  dark:!bg-navy-700 dark:text-white">
            <h2 className="text-4xl font-semibold mb-6 text-center">Placement Count and Process</h2>

            {/* Button Group */}
            <div className="flex flex-col items-center">
                <div className="space-x-4 flex">
                    <button
                        className={`px-4 py-2 rounded-md ${activeTab === "main" ? "bg-purple-500" : "bg-gray-500"} text-white`}
                        onClick={() => setActiveTab("main")}
                    >
                        Placement Count
                    </button>
                    <button
                        className={`px-4 py-2 rounded-md ${activeTab === "sub" ? "bg-purple-500" : "bg-gray-500"} text-white`}
                        onClick={() => setActiveTab("sub")}
                    >
                        Placement Process
                    </button>
                </div>
            </div>

            {/* Placement Count Display */}
            <div className="mt-6">
                {activeTab === "main" && (
                    <>
                        <h2 className="mt-5 text-xl font-bold text-center mb-4">Placement Count</h2>

                        {loading ? (
                            <div className="text-center py-4 text-purple-500">Loading...</div>
                        ) : (
                            <>
                                <button
                                    className="rounded-lg px-7 py-2 text-white bg-green-700 font-large text-large transition duration-200 hover:bg-green-800 float-end"
                                    onClick={openModal}
                                    aria-label="Add Placement Count"
                                >
                                    Add +
                                </button>

                                {/* Add Modal */}
                                {isModalOpen && (
                                    <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
                                        <div className="bg-white p-6 rounded-lg w-[600px] dark:!bg-navy-700 dark:text-white">
                                            <h2 className="text-lg font-bold mb-4">Add Placement Count</h2>
                                            <input
                                                type="text"
                                                className="w-full border p-2 mb-2 dark:!bg-navy-700 dark:text-white"
                                                placeholder="Year"
                                                value={year}
                                                onChange={(e) => setYear(e.target.value)}
                                            />
                                            <input
                                                type="number"
                                                className="w-full border p-2 mb-2 dark:!bg-navy-700 dark:text-white"
                                                placeholder="Count"
                                                value={count}
                                                onChange={(e) => setCount(e.target.value)}
                                            />
                                            <div className="flex justify-end space-x-2">
                                                <button className="px-4 py-2 bg-gray-300 rounded-lg" onClick={closeModal}>Cancel</button>
                                                <button className="px-4 py-2 bg-blue-500 text-white rounded-lg" onClick={handleSave}>Save</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <br /> <br />

                                <div className="overflow-x-auto">
                                    <table className="table-auto w-full">
                                        <thead>
                                            <tr className="bg-gray-100 text-left dark:bg-gray-800 dark:text-white">
                                                <th className="px-4 py-2">Year</th>
                                                <th className="px-4 py-2">Count</th>
                                                <th className="px-4 py-2 text-center">Edit</th>
                                                <th className="px-4 py-2 text-center">Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {placementCounts.length > 0 ? (
                                                placementCounts.map((count) => (
                                                    <tr key={count.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                                        <td className="px-4 py-2">{count.year || "N/A"}</td>
                                                        <td className="px-4 py-2">{count.count || "N/A"}</td>
                                                        <td className="px-4 py-2 text-center">
                                                            <button
                                                                className="rounded-lg px-4 py-2 text-green-700 transition duration-200 hover:bg-white-700 "
                                                                onClick={() => openEditModal(count)}
                                                                aria-label="Edit"
                                                            >
                                                                <MdEdit className='text-green-700 text-2xl' />
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-2 text-center">
                                                            <button
                                                                className="ml-4"
                                                                onClick={() => handleDeleteCount(count.id)}
                                                                aria-label="Delete"
                                                            >
                                                                <MdDelete className='text-red-700 text-2xl' />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="px-4 py-2 text-center text-red-500">
                                                        No placement counts found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {/* Edit Modal */}
                        {isEditModalOpen && (
                            <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
                                <div className="bg-white p-6 rounded-lg w-[600px]">
                                    <h2 className="text-lg font-bold mb-4">Edit Placement Count</h2>
                                    <input
                                        type="text"
                                        className="w-full border p-2 mb-2"
                                        value={editYear}
                                        onChange={(e) => setEditYear(e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        className="w-full border p-2 mb-2"
                                        value={editCount}
                                        onChange={(e) => setEditCount(e.target.value)}
                                    />
                                    <div className="flex justify-end space-x-2">
                                        <button className="px-4 py-2 bg-gray-300 rounded-lg" onClick={closeEditModal}>Cancel</button>
                                        <button className="px-4 py-2 bg-blue-500 text-white rounded-lg" onClick={handleEditSave}>Save</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === "sub" && (
                   <PlacementProcess/>
                )}
            </div>

            {/* Toast Notifications */}
            <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true} />
        </div>
    );
};

export default PlacementCount;